import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Pencil, Plus, Loader2, Trash2 } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  adminService,
  ShippingCost as ShippingCostType,
} from "../../services/adminService";
import { toast } from "sonner";
import { showErrorToast } from "@/utils/errorHandler";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const defaultForm = {
  country: "",
  currency: "USD",
  base_cost: "",
  additional_cost: "",
};

const formatBaseCost = (
  value: string | number | null | undefined,
  currency: string
) => {
  const num =
    value !== null && value !== undefined ? parseFloat(String(value)) : 0;
  if (currency === "INR") {
    return `Rs. ${num.toFixed(2)}`;
  }
  return `$${num.toFixed(2)}`;
};

const ShippingManagement = () => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ShippingCostType | null>(
    null
  );
  const [costs, setCosts] = useState<ShippingCostType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(defaultForm);
  const [deleteTarget, setDeleteTarget] = useState<ShippingCostType | null>(
    null
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const fetchCosts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminService.getShippingCosts({
        page_size: 100,
        ordering: "country",
      });
      setCosts(data?.results ?? []);
    } catch (error: unknown) {
      console.error("Error fetching shipping costs:", error);
      showErrorToast(error, "Failed to load shipping costs. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCosts();
  }, [fetchCosts]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleOpenAdd = () => {
    setFormData(defaultForm);
    setAddDialogOpen(true);
  };

  const handleOpenEdit = (item: ShippingCostType) => {
    setSelectedItem(item);
    setFormData({
      country: item.country ?? "",
      currency: item.currency ?? "USD",
      base_cost:
        item.base_cost !== null && item.base_cost !== undefined
          ? String(item.base_cost)
          : "",
      additional_cost:
        item.additional_cost !== null && item.additional_cost !== undefined
          ? String(item.additional_cost)
          : "",
    });
    setEditDialogOpen(true);
  };

  const handleSaveNew = async () => {
    const baseCost = parseFloat(formData.base_cost);
    const additionalCost = parseFloat(formData.additional_cost);
    if (isNaN(baseCost) || isNaN(additionalCost)) {
      toast.error(
        "Please enter valid numbers for Base Cost and Additional Cost"
      );
      return;
    }
    if (!formData.country.trim()) {
      toast.error("Please enter country");
      return;
    }
    try {
      setSaving(true);
      await adminService.createShippingCost({
        country: formData.country.trim(),
        currency: formData.currency || "USD",
        base_cost: baseCost,
        additional_cost: additionalCost,
      });
      toast.success("Shipping cost added successfully");
      setAddDialogOpen(false);
      setFormData(defaultForm);
      fetchCosts();
    } catch (error: unknown) {
      showErrorToast(error, "Failed to add shipping cost");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedItem) {
      return;
    }
    const baseCost = parseFloat(formData.base_cost);
    const additionalCost = parseFloat(formData.additional_cost);
    if (isNaN(baseCost) || isNaN(additionalCost)) {
      toast.error(
        "Please enter valid numbers for Base Cost and Additional Cost"
      );
      return;
    }
    if (!formData.country.trim()) {
      toast.error("Please enter country");
      return;
    }
    try {
      setSaving(true);
      await adminService.updateShippingCost(selectedItem.id, {
        country: formData.country.trim(),
        currency: formData.currency || "USD",
        base_cost: baseCost,
        additional_cost: additionalCost,
      });
      toast.success("Shipping cost updated successfully");
      setEditDialogOpen(false);
      setSelectedItem(null);
      fetchCosts();
    } catch (error: unknown) {
      showErrorToast(error, "Failed to update shipping cost");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (item: ShippingCostType) => {
    setDeleteTarget(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) {
      return;
    }
    try {
      await adminService.deleteShippingCost(deleteTarget.id);
      toast.success("Shipping cost deleted successfully");
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchCosts();
    } catch (error: unknown) {
      showErrorToast(error, "Failed to delete shipping cost");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-5 md:space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-foreground md:text-3xl">
            Manage Shipping Cost
          </h2>
          <Button
            onClick={handleOpenAdd}
            className="bg-[#654321] text-white hover:bg-[#543210]"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add New
          </Button>
        </div>

        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    S. No.
                  </th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    country
                  </th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    Currency
                  </th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    Base Cost
                  </th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    Additional Cost
                  </th>
                  <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
                      <p className="mt-2 text-muted-foreground">Loading...</p>
                    </td>
                  </tr>
                ) : costs.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-12 text-center text-muted-foreground"
                    >
                      No shipping costs configured. Click &quot;Add New&quot; to
                      add one.
                    </td>
                  </tr>
                ) : (
                  costs.map((item, idx) => (
                    <tr key={item.id} className="hover:bg-muted/30">
                      <td className="whitespace-nowrap px-6 py-3 text-sm">
                        {idx + 1}
                      </td>
                      <td className="whitespace-nowrap px-6 py-3 text-sm font-medium">
                        {item.country}
                      </td>
                      <td className="whitespace-nowrap px-6 py-3 text-sm">
                        {item.currency}
                      </td>
                      <td className="whitespace-nowrap px-6 py-3 text-sm">
                        {formatBaseCost(item.base_cost, item.currency ?? "USD")}
                      </td>
                      <td className="whitespace-nowrap px-6 py-3 text-sm">
                        {item.additional_cost !== null
                          ? `${parseFloat(String(item.additional_cost)).toFixed(0)}% of total cost`
                          : "—"}
                      </td>
                      <td className="whitespace-nowrap px-6 py-3">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenEdit(item)}
                            className="text-[#654321] hover:bg-[#654321]/10"
                          >
                            <Pencil className="mr-1 h-3.5 w-3.5" />
                            Update
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteClick(item)}
                            className="text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="mr-1 h-3.5 w-3.5" />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add Dialog */}
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Shipping Cost</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="add-country" required>
                  country:
                </Label>
                <Input
                  id="add-country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  placeholder="e.g. USA, IND, Canada"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-currency" required>
                  Currency:
                </Label>
                <Input
                  id="add-currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  placeholder="USD or INR"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-base_cost" required>
                  Base Cost:
                </Label>
                <Input
                  id="add-base_cost"
                  name="base_cost"
                  type="number"
                  step="0.01"
                  value={formData.base_cost}
                  onChange={handleInputChange}
                  placeholder="e.g. 2.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-additional_cost" required>
                  Additional Cost: %
                </Label>
                <Input
                  id="add-additional_cost"
                  name="additional_cost"
                  type="number"
                  step="0.01"
                  value={formData.additional_cost}
                  onChange={handleInputChange}
                  placeholder="e.g. 15"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveNew}
                disabled={saving}
                className="bg-[#654321] text-white hover:bg-[#543210]"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Update Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Update Shipping Cost</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-country" required>
                  country:
                </Label>
                <Input
                  id="edit-country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  placeholder="e.g. USA, IND, Canada"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-currency" required>
                  Currency:
                </Label>
                <Input
                  id="edit-currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  placeholder="USD or INR"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-base_cost" required>
                  Base Cost:
                </Label>
                <Input
                  id="edit-base_cost"
                  name="base_cost"
                  type="number"
                  step="0.01"
                  value={formData.base_cost}
                  onChange={handleInputChange}
                  placeholder="e.g. 2.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-additional_cost" required>
                  Additional Cost: %
                </Label>
                <Input
                  id="edit-additional_cost"
                  name="additional_cost"
                  type="number"
                  step="0.01"
                  value={formData.additional_cost}
                  onChange={handleInputChange}
                  placeholder="e.g. 15"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdate}
                disabled={saving}
                className="bg-[#654321] text-white hover:bg-[#543210]"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Shipping Cost</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete the shipping cost for{" "}
                {deleteTarget?.country}? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default ShippingManagement;
